import React from 'react';
import { FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { TLicenseSelectProps } from '@/console/features/licenses/components/license-select/license-select.types';
import { TSelectOption } from '@/shared/components/ui/form/components/searchable-select/base-searchable-select.types';
import { AsyncSearchableSelect } from '@/shared/components/ui/form/components/searchable-select/variant/async/async-searchable-select';
import { useReadAllLicensesQuery } from '@/shared/redux/api/assets/licenses/licenses-api';
import {
  LicenseFragment,
  ReadAllLicensesQuery,
} from '@/shared/types/types.generated';

export const LicenseSelect = <
  TFieldValues extends FieldValues,
  TOptionShape = string,
>({
  name,
  createOption,
  getItemId,
  disableTags = true,
  enableTagsTooltip,
  filterSelectedTags,
  additionalOptions = [],
  onSelectChange,
  required,
}: TLicenseSelectProps<TFieldValues, TOptionShape>) => {
  const { t } = useTranslation();

  const createDefaultOption = (license: LicenseFragment) => {
    return {
      label: license.product,
      value: license.licenseId,
      additionalInfo: license.status
        ? t(`licenses:license_status_enum.${license.status.toLowerCase()}`)
        : t('licenses:unknown'),
    } as TSelectOption<TOptionShape>;
  };
  const _createOption = createOption ? createOption : createDefaultOption;

  const transformData = (data?: ReadAllLicensesQuery) => {
    if (!data) {
      return [];
    }

    const licenses = data?.readAllLicenses?.items;

    const options = licenses.map((license) => _createOption(license));

    return [...additionalOptions, ...options];
  };

  return (
    <AsyncSearchableSelect<TFieldValues, ReadAllLicensesQuery, TOptionShape>
      required={required}
      name={name}
      label={t('common:license')}
      multiple
      disableTags={disableTags}
      enableTagsTooltip={enableTagsTooltip}
      filterSelectedTags={filterSelectedTags}
      getItemId={getItemId}
      optionProps={{
        useRTKQuery: useReadAllLicensesQuery,
        transformData,
      }}
      onSelectChange={onSelectChange}
    />
  );
};
